import { useEffect, useState } from "preact/hooks";
import styles from "./CountdownCircle.module.scss";

export interface CountdownCircleProps {
  size?: number;
  secondsLeft: number;
  duration: number;
}

export const CountdownCircle = ({
  size = 110,
  secondsLeft,
  duration,
}: CountdownCircleProps) => {
  // Disable initial transition so arc doesn't grow from 0 on first paint
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // next tick enables transitions for subsequent updates
    setMounted(true);
  }, []);
  const R_OUT = 61.0625; // from SVG (65 - 3.9375)
  const R_IN = 54.5; // from SVG (65 - 10.5)

  const clamped = Math.max(0, Math.min(secondsLeft, duration));

  const epsilon = 0.0001;
  const denom = Math.max(1, duration - 1);
  const progress = Math.max(0, Math.min(1, (clamped - 1 + epsilon) / denom));

  const VBW = 131;
  const VBH = 132;
  const CX = 65.5;
  const CY = 66;

  const circumference = 2 * Math.PI * ((R_OUT + R_IN) / 2);
  const dash = circumference * progress;
  const gap = Math.max(0.0001, circumference - dash);

  const dashOffset = 0;

  return (
    <div className={styles.wrapper} style={{ width: size, height: size }}>
      <svg
        className={styles.svg}
        width={size}
        height={size}
        viewBox={`0 0 ${VBW} ${VBH}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M128 66C128 100.794 100.018 129 65.5 129C30.9822 129 3 100.794 3 66C3 31.2061 30.9822 3 65.5 3C100.018 3 128 31.2061 128 66ZM10.3212 66C10.3212 96.7182 35.0256 121.62 65.5 121.62C95.9744 121.62 120.679 96.7182 120.679 66C120.679 35.2818 95.9744 10.3797 65.5 10.3797C35.0256 10.3797 10.3212 35.2818 10.3212 66Z"
          fill="#707071"
        />

        <g className={styles.progress} transform={`rotate(-90 ${CX} ${CY})`}>
          <circle
            className={mounted ? styles.arc : styles.arcNoAnim}
            cx={CX}
            cy={CY}
            r={(R_OUT + R_IN) / 2}
            fill="none"
            stroke="#FFD700"
            strokeWidth={10}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={dashOffset}
          />
        </g>

        <mask
          id="ringMask"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="131"
          height="132"
          fill="black"
        >
          <rect fill="white" width="131" height="132" />
          <path d="M128 66C128 100.794 100.018 129 65.5 129C30.9822 129 3 100.794 3 66C3 31.2061 30.9822 3 65.5 3C100.018 3 128 31.2061 128 66ZM10.3212 66C10.3212 96.7182 35.0256 121.62 65.5 121.62C95.9744 121.62 120.679 96.7182 120.679 66C120.679 35.2818 95.9744 10.3797 65.5 10.3797C35.0256 10.3797 10.3212 35.2818 10.3212 66Z" />
        </mask>
        <path
          d="M128 66C128 100.794 100.018 129 65.5 129C30.9822 129 3 100.794 3 66C3 31.2061 30.9822 3 65.5 3C100.018 3 128 31.2061 128 66ZM10.3212 66C10.3212 96.7182 35.0256 121.62 65.5 121.62C95.9744 121.62 120.679 96.7182 120.679 66C120.679 35.2818 95.9744 10.3797 65.5 10.3797C35.0256 10.3797 10.3212 35.2818 10.3212 66Z"
          stroke="#262624"
          strokeWidth={6}
          mask="url(#ringMask)"
        />
      </svg>

      <div className={styles.label}>{Math.max(0, Math.ceil(clamped))}</div>
    </div>
  );
};
